import React, { useEffect, useState } from 'react';
import {
  AppBar, Toolbar, Container, Box, IconButton, Drawer, List, ListItem, ListItemText,
  Collapse, Divider, useMediaQuery, useTheme, Grid, Popover, Menu, MenuItem, Badge, ListItemIcon, ListItemButton
} from '@mui/material';
import {
  Menu as MenuIcon, Close as CloseIcon, AccountCircle,
  ExpandMore as ExpandMoreIcon, ContactMail as ContactMailIcon,
  Work as WorkIcon, Help as HelpIcon, Person as PersonIcon,
  MenuBook as MenuBookIcon, Assignment as AssignmentIcon,
  RateReview as RateReviewIcon, Videocam as VideocamIcon,
  ShoppingCart as ShoppingCartIcon, ExitToApp as ExitToAppIcon
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from "../../context/CardContext";
import lmsLogo from '../../assets/images/logobare.png';
import logo from '../../assets/images/lmsLogo.png';
import {
  CustomAppBar, NavLinkWrapper, NavButton, CoursesDropdownPaper,
  MoreDropdownPaper, CourseCategory, CourseItem, MoreMenuItem,
  AuthButton, MobileDrawer, MobileNavItem
} from './headerStyles';

const coursesData = {
  free: [
    { id: 1, title: 'General Duty Assistant' },
    { id: 2, title: 'Insdustrial Awareness' },
    { id: 3, title: 'First Aid' },
    { id: 4, title: 'Kaizen' },
  ],
  nsqf: [
    { id: 5, title: 'CNC and VMC Operator ' },
    { id: 6, title: 'CNC and VMC Programmer' },
    { id: 7, title: 'Quality Inspector' },
    { id: 8, title: 'Hospitality Management ' },
  ],
  nonNsqf: [
    { id: 9, title: 'HTML (HyperText Markup Language)' },
    { id: 10, title: 'Automative Technology' },
    { id: 11, title: 'MS Office Basics' },
    { id: 12, title: 'Industrial Safety' },
  ],
};

const moreItems = [
  { label: 'Contact Us', path: '/contact-us', icon: <ContactMailIcon /> },
  { label: 'Placements', path: '/placement', icon: <WorkIcon /> },
  { label: 'FAQs', path: '/faq', icon: <HelpIcon /> },
];

const navItems = [
  { label: 'About Us', path: '/about-us' },
  { label: 'Industry Specific', path: '/industry-specific-course' },
  { label: 'Partners', path: '/partner-course' },
  { label: 'TNI', path: '/tni' },
];

const ProfileMenu = ({ mobile = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const { cart } = useCart();
  const cartCount = cart.length;
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = (link) => {
    navigate(link);
    handleClose();
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("student");
    navigate("/");
    handleClose();
  };

  const menuItems = [
    { label: 'Profile', path: '/profile', icon: <PersonIcon /> },
    { label: 'My Courses', path: '/myCourses', icon: <MenuBookIcon /> },
    { label: 'My Exams', path: '/myExams', icon: <AssignmentIcon /> },
    { label: 'My Feedbacks', path: '/myFeedbacks', icon: <RateReviewIcon /> },
    { label: 'Live Classes', path: '/live-classes', icon: <VideocamIcon /> },
    {
      label: `Cart ${cartCount > 0 ? `(${cartCount})` : ''}`,
      path: '/cart',
      icon: (
        <Badge badgeContent={cartCount} color="primary" max={99}>
          <ShoppingCartIcon />
        </Badge>
      )
    },
  ];

  if (mobile) {
    // For mobile, render as a simple list in the drawer without dropdown
    return (
      <List sx={{ p: 0 }}>
        {menuItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton onClick={() => handleProfileClick(item.path)} sx={{ px: 2 }}>
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout} sx={{ px: 2 }}>
            <ListItemIcon sx={{ minWidth: 40, color: 'error' }}>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" sx={{ color: 'error' }} />
          </ListItemButton>
        </ListItem>
      </List>
    );
  }

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          color: '#f7c42d',
          border: '2px solid #fdd649',
          borderRadius: '50%',
          '&:hover': { border: '2px solid #f7c42d' }
        }}
      >
        <AccountCircle fontSize="large" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { mt: 1, minWidth: 200 }
        }}
      >
        {menuItems.map((item) => (
          <MenuItem key={item.path} onClick={() => handleProfileClick(item.path)}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            {item.label}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: 'error' }}>
          <ListItemIcon sx={{ color: 'error' }}>
            <ExitToAppIcon />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [coursesAnchor, setCoursesAnchor] = useState(null);
  const [moreAnchor, setMoreAnchor] = useState(null);
  const [coursesTimeout, setCoursesTimeout] = useState(null);
  const [moreTimeout, setMoreTimeout] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const token = localStorage.getItem('token');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (coursesTimeout) clearTimeout(coursesTimeout);
      if (moreTimeout) clearTimeout(moreTimeout);
    };
  }, [coursesTimeout, moreTimeout]);

  const currentPath = location.pathname;
  const isActive = (paths) => paths.some(p => currentPath === p || currentPath.startsWith(p + '/'));
  const activeTab = (() => {
    if (currentPath === '/') return null;
    const idx = navItems.findIndex(i => isActive([i.path]));
    if (idx !== -1) return idx;
    if (currentPath.includes('/allCourses') || currentPath.includes('/courses')) return 'courses';
    if (moreItems.some(i => isActive([i.path]))) return 'more';
    return null;
  })();
  const openCourses = Boolean(coursesAnchor);
  const openMore = Boolean(moreAnchor);

  // Courses hover handlers with delay
  const handleCoursesEnter = (e) => {
    if (coursesTimeout) clearTimeout(coursesTimeout);
    setCoursesAnchor(e.currentTarget);
  };

  const handleCoursesLeave = () => {
    setCoursesTimeout(
      setTimeout(() => {
        setCoursesAnchor(null);
      }, 250) // 250ms delay for closing
    );
  };

  // More hover handlers with delay
  const handleMoreEnter = (e) => {
    if (moreTimeout) clearTimeout(moreTimeout);
    setMoreAnchor(e.currentTarget);
  };

  const handleMoreLeave = () => {
    setMoreTimeout(
      setTimeout(() => {
        setMoreAnchor(null);
      }, 250) // 250ms delay for closing
    );
  };

  return (
    <>
      {/* Fixed AppBar */}
      <CustomAppBar scrolled={scrolled ? 1 : 0}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 70, md: 90 }, py: 1, justifyContent: 'space-between' }}>
            <Box
              component="img"
              src={logo}
              alt="LMS"
              onClick={() => navigate('/')}
              sx={{ height: { xs: 80, m9: 90 }, cursor: 'pointer' }}
            />
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                {navItems.map((item, i) => (
                  <NavLinkWrapper key={i} to={item.path} active={activeTab === i ? 1 : 0}>
                    {item.label}
                  </NavLinkWrapper>
                ))}
                {/* Courses Trigger */}
                <Box sx={{ position: 'relative' }}>
                  <NavButton
                    onClick={() => navigate('/allCourses/all')}
                    active={activeTab === 'courses' ? 1 : 0}
                  >
                    Courses
                  </NavButton>
                </Box>
                {/* More Trigger */}
                <Box
                  onMouseEnter={handleMoreEnter}
                  onMouseLeave={handleMoreLeave}
                  sx={{ position: 'relative' }}
                >
                  <NavButton
                    active={activeTab === 'more' ? 1 : 0}
                    endIcon={<ExpandMoreIcon sx={{ transform: openMore ? 'rotate(180deg)' : 'rotate(0)', transition: '0.35s ease' }} />}
                  >
                    More
                  </NavButton>
                </Box>
              </Box>
            )}
            {/* Auth */}
            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                {!token ? (
                  <>
                    <AuthButton variant="outlined" onClick={() => navigate('/login')}>Login</AuthButton>
                    <AuthButton variant="contained" onClick={() => navigate('/signup')}>Sign Up</AuthButton>
                  </>
                ) : (
                  <ProfileMenu />
                )}
              </Box>
            )}
            {isMobile && (
              <IconButton onClick={() => setMobileOpen(true)} sx={{ color: '#000' }}>
                <MenuIcon sx={{ fontSize: 36 }} />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </CustomAppBar>
      {/* COURSES DROPDOWN – No flicker, with delay and smooth transition */}
      {/*
      <Popover
        open={openCourses}
        anchorEl={coursesAnchor}
        onClose={() => { }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          component: CoursesDropdownPaper,
          onMouseEnter: () => {
            if (coursesTimeout) {
              clearTimeout(coursesTimeout);
              setCoursesTimeout(null);
            }
          },
          onMouseLeave: handleCoursesLeave,
          sx: {
            transition: 'all 0.2s ease-in-out', // Smooth opening/closing
            opacity: openCourses ? 1 : 0,
            transform: openCourses ? 'scale(1)' : 'scale(0.95)',
            transformOrigin: 'top center',
          },
        }}
        sx={{ mt: '12px', pointerEvents: 'none' }}
        disableRestoreFocus
      >
        <Box sx={{ pointerEvents: 'auto', p: 3 }}>
          <Grid container spacing={4}>
            {Object.entries(coursesData).map(([key, items]) => (
              <Grid item xs={4} key={key}>
                <CourseCategory>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 2, color: '#222' }}>
                    {key === 'nonNsqf' ? 'Non-NSQF' : key.toUpperCase()} Courses
                  </Box>
                  {items.map((c) => (
                    <CourseItem
                      key={c.id}
                      onClick={() => {
                        navigate(`/allCourses/${key === 'nonNsqf' ? 'non-nsqf' : key}`);
                        setCoursesAnchor(null);
                      }}
                    >
                      {c.title}
                    </CourseItem>
                  ))}
                </CourseCategory>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Popover>
      */}
      {/* MORE DROPDOWN – Fixed flicker, with delay and smooth transition */}
      <Popover
        open={openMore}
        anchorEl={moreAnchor}
        onClose={() => { }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          component: MoreDropdownPaper,
          onMouseEnter: () => {
            if (moreTimeout) {
              clearTimeout(moreTimeout);
              setMoreTimeout(null);
            }
          },
          onMouseLeave: handleMoreLeave,
          sx: {
            transition: 'all 0.2s ease-in-out', // Smooth opening/closing
            opacity: openMore ? 1 : 0,
            transform: openMore ? 'scale(1)' : 'scale(0.95)',
            transformOrigin: 'top center',
          },
        }}
        sx={{ mt: '12px', pointerEvents: 'none' }}
        disableRestoreFocus
      >
        <Box sx={{ pointerEvents: 'auto', p: 1 }}>
          {moreItems.map((item) => (
            <MoreMenuItem
              key={item.path}
              onClick={() => {
                navigate(item.path);
                setMoreAnchor(null);
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {item.icon}
                {item.label}
              </Box>
            </MoreMenuItem>
          ))}
        </Box>
      </Popover>
      {/* MOBILE DRAWER – Fixed z-index & close icon visibility */}
      <MobileDrawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.4rem' }}>Menu</Box>
          <IconButton onClick={() => setMobileOpen(false)} sx={{ color: '#000', zIndex: 1500 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List sx={{ px: 2 }}>
          {navItems.map((item, i) => (
            <MobileNavItem key={i} component={Link} to={item.path} onClick={() => setMobileOpen(false)}>
              <ListItemText primary={item.label} />
            </MobileNavItem>
          ))}
          {/* Mobile Courses */}
          <MobileNavItem onClick={() => { navigate('/allCourses/all'); setMobileOpen(false); }}>
            <ListItemText primary="Courses" />
          </MobileNavItem>
          {/* Mobile More Items */}
          {moreItems.map((item) => (
            <MobileNavItem key={item.path} onClick={() => { navigate(item.path); setMobileOpen(false); }}>
              {item.icon}
              <ListItemText primary={item.label} sx={{ ml: 2 }} />
            </MobileNavItem>
          ))}
        </List>
        <Box sx={{ p: 3, mt: 'auto' }}>
          {!token ? (
            <>
              <AuthButton fullWidth variant="outlined" sx={{ mb: 2 }} onClick={() => { navigate('/login'); setMobileOpen(false); }}>
                Login
              </AuthButton>
              <AuthButton fullWidth variant="contained" onClick={() => { navigate('/signup'); setMobileOpen(false); }}>
                Sign Up
              </AuthButton>
            </>
          ) : (
            <ProfileMenu mobile={true} />
          )}
        </Box>
      </MobileDrawer>
      <Box sx={{ height: { xs: 90, md: 100 } }} />
    </>
  );
}